// **What a resumed practice sandbox reconstructs itself from** (`client-practice`,
// `rounds-local-trace-store`).
//
// A tournament sandbox is reconstructed by asking the server: `GET /rounds/{scope}/versions` for the
// history and `GET /rounds/{scope}/runs` for the runs that attach to it. A *practice* sandbox gets
// only the first half of that. The run list is a tournament-scope route — its handler resolves the
// path through `parse_scope_id`, which answers `None` for a practice identifier — so it is a `404`
// for every practice scope, and `RoundsHttpClient.listRuns` turns a `404` into an empty list rather
// than an error. The version menu was therefore built from *no* candidates and read "not run" over
// every version, on a device that was still holding that version's trace.
//
// The device holds the other half, and always did: the local trace store's run index survives the
// reload the coordinator's in-memory state does not. This function is that index read as the thing the
// resumed screen needs — the menu's candidates — so the reconstruction is one mapping in one place
// rather than an ad-hoc one in the composition root.
//
// **`home-and-single-player` deleted its sibling, `practiceResumeConfig`.** That one recovered the
// newest run's frozen `(rivals, seed)` so a resumed session's status pill could state it. There is no
// per-session configuration left to recover: the rival count is fixed at three and the seed is server
// configuration, delivered in the run-start response rather than chosen and frozen on the device.
//
// Pure: records in, plain data out. No store, no fetch, no clock.

import type { PracticeRunRecord } from './practice-runner';
import type { MenuCandidate } from '../../ui/rounds/version-menu-model';

/**
 * The device's runs as version-menu candidates.
 *
 * Every stored row is `completed`: the store's index is written at publication, which happens only
 * after a run reaches its terminal tick — a cancelled or crashed run reclaims its generation and
 * writes no row at all. So the status is not guessed, it is the only status a stored row can have.
 *
 * An **evicted** row is included rather than filtered. It keeps its score (that is the whole point
 * of the row surviving eviction), so hiding it would lose a run the player made; selecting it
 * resolves to the store's own "this run's replay was cleared" refusal, which is the surface that
 * already exists for exactly that.
 */
export function practiceMenuCandidates(
  runs: readonly PracticeRunRecord[],
): readonly MenuCandidate[] {
  return runs.map((run) => ({
    runId: run.runId,
    documentVersion: run.documentVersion,
    status: 'completed' as const,
    score: run.score,
  }));
}

